import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../services/supabase'
import { useAuthStore } from '../../store/authStore'
import { disconnectSocket } from '../../services/socket'
import { Zap, User, Settings, LogOut, Menu, X, ChevronRight, HelpCircle } from 'lucide-react'
import { XP_PER_LEVEL, getMaxZones, LEVEL_CAPS } from '@territory-nexus/shared'
import InfoPanel from './InfoPanel'
import SettingsPanel from './SettingsPanel'

export default function TopBar() {
  const navigate = useNavigate()
  const [showPanel, setShowPanel] = useState(false)
  const [showInfo, setShowInfo] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const { player, logout } = useAuthStore()

  const handleLogout = async () => {
    setShowPanel(false)
    try {
      disconnectSocket()
      logout()
      await supabase.auth.signOut()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      navigate('/login')
    }
  }

  return (
    <>
      <header className="relative z-20 bg-slate-900/80 backdrop-blur-xl border-b border-white/10 safe-area-top">
        <div className="flex items-center justify-between px-3 py-2">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-cyan-400" />
            <h1 className="text-sm font-display font-bold">
              <span className="text-gradient-cyan">TERRITORY NEXUS</span>
            </h1>
          </div>

          {/* Player Info + Menu */}
          <button
            onClick={() => setShowPanel(true)}
            className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-white/5 transition-colors"
          >
            <div className="text-right">
              <div className="text-[9px] text-slate-500 font-mono">CMD</div>
              <div className="flex items-center gap-1.5 justify-end">
                <div className="text-xs font-semibold text-slate-200">{player?.username ?? '...'}</div>
                {player && (
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-cyan-500/20 text-[10px] font-bold text-cyan-400 border border-cyan-500/30">
                    {player.level}
                  </span>
                )}
              </div>
            </div>
            <Menu className="w-5 h-5 text-slate-400" />
          </button>
        </div>
      </header>

      {/* Slide-out Panel */}
      {showPanel && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm"
            onClick={() => setShowPanel(false)}
          />
          
          {/* Panel */}
          <div className="fixed top-0 right-0 bottom-0 z-[101] w-64 bg-slate-900 border-l border-white/10 shadow-2xl animate-slide-in-right">
            {/* Panel Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <span className="text-sm font-semibold text-slate-300">Menu</span>
              <button
                onClick={() => setShowPanel(false)}
                className="p-1 rounded hover:bg-white/10 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Player Card */}
            <div className="p-4 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="relative w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white font-bold">
                  {player?.username?.charAt(0).toUpperCase() ?? '?'}
                  {player && (
                    <span className="absolute -bottom-1 -right-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-900 text-[10px] font-bold text-cyan-400 border border-cyan-500/40">
                      {player.level}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-white">{player?.username}</div>
                  <div className="text-xs text-slate-400 capitalize">{player?.faction} Faction</div>
                </div>
              </div>

              {/* XP Progress */}
              {player && (
                <div className="mt-3 space-y-1.5">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-400">
                      Level {player.level}{player.level >= LEVEL_CAPS.player ? ' (MAX)' : ''}
                    </span>
                    {player.level < LEVEL_CAPS.player && (
                      <span className="text-slate-500">
                        {player.xp} / {XP_PER_LEVEL(player.level)} XP
                      </span>
                    )}
                  </div>
                  {player.level < LEVEL_CAPS.player && (
                    <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 transition-all"
                        style={{ width: `${Math.min(100, (player.xp / XP_PER_LEVEL(player.level)) * 100)}%` }}
                      />
                    </div>
                  )}
                  <div className="text-xs text-slate-500">
                    Max zones: {getMaxZones(player.level)}
                  </div>
                </div>
              )}
            </div>

            {/* Menu Items */}
            <div className="py-2">
              <button
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/5 transition-colors"
                onClick={() => {
                  setShowPanel(false)
                  setShowInfo(true)
                }}
              >
                <div className="flex items-center gap-3">
                  <HelpCircle className="w-5 h-5 text-cyan-400" />
                  <span>How to Play</span>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-600" />
              </button>
              
              <button
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/5 transition-colors"
                onClick={() => setShowPanel(false)}
              >
                <div className="flex items-center gap-3">
                  <User className="w-5 h-5 text-slate-400" />
                  <span>Profile</span>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-600" />
              </button>
              
              <button
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/5 transition-colors"
                onClick={() => {
                  setShowPanel(false)
                  setShowSettings(true)
                }}
              >
                <div className="flex items-center gap-3">
                  <Settings className="w-5 h-5 text-slate-400" />
                  <span>Settings</span>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-600" />
              </button>
            </div>

            {/* Logout */}
            <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
              <button
                onClick={handleLogout}
                className="w-full px-4 py-2.5 flex items-center justify-center gap-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span className="text-sm font-medium">Logout</span>
              </button>
            </div>
          </div>
        </>
      )}

      {/* Info Panel */}
      {showInfo && <InfoPanel onClose={() => setShowInfo(false)} />}

      {/* Settings Panel */}
      {showSettings && <SettingsPanel onClose={() => setShowSettings(false)} />}
    </>
  )
}
